import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Divider, Row, Col } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, TeamOutlined, BookOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import './AuthPage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * OIDC Authentication page for signin/signup
 */
const AuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState(null);

  // Redirect if already authenticated - go directly to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next');
      
      // If there's a specific next URL, use it
      if (nextUrl) {
        history.replace(nextUrl);
        return;
      }
      
      // For authenticated users, go directly to admin dashboard
      console.log('User is authenticated - redirecting to admin dashboard');
      history.replace('/admin');
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      // Set the error in the auth context
      clearError(); // Clear any existing error first
      setTimeout(() => {
        // Use setTimeout to ensure the error is set after the component mounts
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login
  const handleOIDCLogin = () => {
    clearError();
    login();
  };


  const handleOIDCRegister = () => {
    clearError();
    register();
  };

  // Handle role selection
  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    console.log('Selected role:', role);
  };

  // Handle OIDC login with selected role
  const handleOIDCLoginWithRole = () => {
    clearError();
    login(selectedRole);
  };

  // Handle OIDC register with selected role
  const handleOIDCRegisterWithRole = () => {
    clearError();
    register(selectedRole);
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }


  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main OIDC signin/signup page
  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-content">
          <div className="auth-header">
            <UserOutlined className="auth-icon" />
            <Title level={1} className="auth-title">
              Welcome Back
            </Title>
            <Text className="auth-subtitle">
              Sign in to access your dashboard
            </Text>
          </div>

          {/* Role Selection for unauthenticated users */}
          {!isAuthenticated && (
            <div className="role-selection">
              <Title level={4} style={{ marginBottom: '32px', textAlign: 'center' }}>
                Choose Your Role
              </Title>
              <Row gutter={[16, 16]} justify="center">
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <Button 
                    type={selectedRole === 'student' ? 'primary' : 'default'}
                    size="large" 
                    onClick={() => handleRoleSelection('student')}
                    className="role-button"
                    block
                  >
                    <div className="role-button-content">
                      <div className="role-icon-container">
                        <BookOutlined />
                      </div>
                      <div className="role-text-container">
                        <div className="role-title">Student</div>
                        <div className="role-description">Access learning materials</div>
                      </div>
                    </div>
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <Button 
                    type={selectedRole === 'client' ? 'primary' : 'default'}
                    size="large" 
                    onClick={() => handleRoleSelection('client')}
                    className="role-button"
                    block
                  >
                    <div className="role-button-content">
                      <div className="role-icon-container">
                        <TeamOutlined />
                      </div>
                      <div className="role-text-container">
                        <div className="role-title">Client</div>
                        <div className="role-description">Manage organization</div>
                      </div>
                    </div>
                  </Button>
                </Col>
                
                {/* Commented out for now - can be enabled later */}
                {/* 
                <Col xs={24} sm={12} md={10} lg={8}>
                  <Button 
                    type={selectedRole === 'admin' ? 'primary' : 'default'}
                    size="large" 
                    onClick={() => handleRoleSelection('admin')}
                    className="role-button"
                    icon={<CrownOutlined />}
                    block
                  >
                    <div className="role-button-content">
                      <div className="role-title">Administrator</div>
                      <div className="role-description">Full system access and management</div>
                    </div>
                  </Button>
                </Col>
                <Col xs={24} sm={12} md={10} lg={8}>
                  <Button 
                    type={selectedRole === 'onboarding' ? 'primary' : 'dashed'}
                    size="large" 
                    onClick={() => handleRoleSelection('onboarding')}
                    className="role-button"
                    icon={<UserOutlined />}
                    block
                  >
                    <div className="role-button-content">
                      <div className="role-title">Complete Profile</div>
                      <div className="role-description">Set up your profile first</div>
                    </div>
                  </Button>
                </Col>
                */}
              </Row>
            </div>
          )}

          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={selectedRole ? handleOIDCLoginWithRole : handleOIDCLogin}
              className="auth-button oidc-button"
              icon={<LockOutlined />}
              loading={isLoading}
              disabled={!selectedRole && !isAuthenticated}
            >
              {selectedRole ? `Sign In as ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}` : 'Sign In with OIDC'}
            </Button>
            
            <Divider>or</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={selectedRole ? handleOIDCRegisterWithRole : handleOIDCRegister}
              className="auth-button signup-button"
              icon={<UserOutlined />}
              disabled={!selectedRole && !isAuthenticated}
            >
              {selectedRole ? `Create ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)} Account` : 'Create New Account'}
            </Button>
          </div>

          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Secure authentication powered by Stride ID
            </Paragraph>
            <Paragraph className="auth-info">
              You will be redirected to your identity provider for secure sign-in
            </Paragraph>
          </div>

          <div className="auth-footer">
            <Text type="secondary">
              Having trouble? Contact your administrator
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AuthPage; 